import React, { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Button from "@laborhack/custom-button";
import {
  Link,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { logEvent } from "../../../helpers/analytics";

import businessCardImage from "assets/business-user-card.svg";
import artisanCardImage from "assets/artisan-user-card.svg";
import homeOwnerCardImage from "assets/homeowner-user-card.svg";
import clsx from "clsx";
import { useQuery } from "../../../hooks";
import UserTypeCard from "../components/UserTypeCard";

import styles from "./styles.module.scss";

import FederatedIdentitySignInForm from "../components/FederatedIDPSignIn";
import { Divider } from "../../../_components/Divider";
import EmailPasswordSignInForm from "../components/EmailPasswordSignIn";
import ArtisanSignUp from "../components/ArtisanSignUp";
import { useApplicationConfig } from "../../../context/ApplicationConfig";
import BusinessSignUp from "../components/BusinessSignUp";
import { Icon } from "semantic-ui-react";
import { useAuth0 } from "@auth0/auth0-react";

export interface RegisterProps {}

export const Register: FC<RegisterProps> = ({}) => {
  const { loginWithRedirect, isLoading } = useAuth0();
  const { path } = useRouteMatch();

  const { toggles } = useApplicationConfig();

  const { pathname } = useLocation();

  /**
   * Get the current user type route from url
   * /auth/register/<THIS_ITEM>
   */
  const selectedUserType = pathname.split("/")[3];

  const history = useHistory();
  const query = useQuery();
  const hideSelection = Boolean(query.get("hide_selection"));

  const continueUrl = query.get("continue");

  continueUrl && localStorage.setItem("continue_url_after_signin", continueUrl);

  const [userType, setUserType] = useState<string>(selectedUserType);

  const userTypeCardProps = [
    {
      text: "Company",
      image: businessCardImage,
      className: "bg-[#2978B1]",
      inactiveBgColor: "bg-[#D2E8E9]",
      value: "business",
    },
    {
      text: "Artisan",
      image: artisanCardImage,
      className: "bg-[#29B19A]",
      inactiveBgColor: "bg-[#D8EBDF]",
      value: "pro",
    },
    {
      text: "Homeowner",
      image: homeOwnerCardImage,
      className: "bg-accent-500",
      inactiveBgColor: "bg-[#F5E0D5]",
      value: "homeowner",
    },
  ];

  const handleUserTypeChange = (value: string) => {
    setUserType(value);
    history.push(`${path.replace("/:id", "")}/${value}`);
  };

  const handleHomeownerLogin = () => {
    loginWithRedirect({
      redirectUri: window.location.origin + "/auth/finishSignIn",
    });
  };

  useEffect(() => {
    logEvent("page_view");
  }, []);

  return (
    <>
      <Helmet>
        <title>Register | LaborHack</title>
      </Helmet>
      <div className={styles.register}>
        <h1 className="text-3xl mb-4">Get Started</h1>
        <p>
          Already have an account? <Link to="/auth/login">Sign In</Link>
        </p>

        <div className="my-8">
          {!hideSelection && (
            <>
              <h2 className="text-xl font-semibold text-primary-500 mb-4">
                Register As A:
              </h2>
              <div className="flex flex-col md:flex-row">
                {userTypeCardProps.map((props) => {
                  const isActive = userType === props.value;

                  return (
                    <UserTypeCard
                      key={props.text}
                      {...props}
                      isActive={isActive}
                      className={clsx(
                        props.className,
                        !isActive && `${props.inactiveBgColor}`
                      )}
                      onClick={() => handleUserTypeChange(props.value)}
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>

        <Switch>
          <Route path={`${path}/homeowner`}>
            {toggles?.use_auth0_explicitly ? (
              <div>
                <h3 className="text-primary-500 font-bold mb-2">
                  Homeowner Sign In
                </h3>
                <Button loading={isLoading} onClick={handleHomeownerLogin}>
                  Sign Up to LaborHack
                  <Icon size="large" name="long arrow alternate right" />
                </Button>
              </div>
            ) : (
              <>
                <FederatedIdentitySignInForm isRegistration />
                <Divider className={styles.divider} text="or" />
                <EmailPasswordSignInForm />
              </>
            )}
          </Route>
          <Route path={`${path}/pro`}>
            <ArtisanSignUp />
          </Route>
          <Route path={`${path}/business`}>
            <BusinessSignUp />
          </Route>
        </Switch>
      </div>
    </>
  );
};
